import * as React from 'react'
import styled, { css } from 'styled-components'
import theme from 'theme'
import { ReactComponent as PlusIcon } from 'assets/plus.svg'
import { ReactComponent as MinusIcon } from 'assets/minus.svg'

type Props = {
  ref: React.RefObject<HTMLElement | null>
  minValue?: number
  maxValue?: number
  customTailorInitialNullValue?: boolean
  disabled?: boolean
  setValue: (value: number) => void
  value?: number
  'data-cy-handle-increase'?: string
  'data-cy-handle-decrease'?: string
  'data-cy-handle-amount'?: string
  decrease_button_a11y_title: string
  amount_input_a11y_title: string
  increase_button_a11y_title: string
}

// TODO
export default function Amount(props: Props) {
  const [value, setValue] = React.useState(
    props.customTailorInitialNullValue
      ? null
      : props.minValue
        ? props.minValue
        : 0
  )
  const [plusDisabled, setPlusDisabled] = React.useState(false)
  const [minusDisabled, setMinusDisabled] = React.useState(
    props.minValue !== undefined && props.minValue === value ? true : false
  )
  const $input = React.useRef<HTMLInputElement>(null)
  const $amountButtonIncrease = React.useRef<HTMLButtonElement>(null)
  const $amountButtonDecrease = React.useRef<HTMLButtonElement>(null)

  const increment = () => {
    if (props.disabled === true) {
      return
    }
    if (props.customTailorInitialNullValue) {
      setValue(props.minValue || 0)
      props.setValue(props.minValue || 0)
    }
    if (props.maxValue && value) {
      if (props.maxValue > value) {
        setValue(value + 1)
        value && props.setValue(value + 1)
      }
      if (props.maxValue <= value + 1) {
        setPlusDisabled(true)
      }
    } else if (value) {
      setValue(value + 1)
      value && props.setValue(value + 1)
    }
  }

  const decrement = () => {
    if (props.disabled === true) {
      return
    }
    if (props.minValue !== undefined && value) {
      if (props.minValue < value) {
        setValue(value - 1)
        value && props.setValue(value - 1)
      }
      if (props.minValue >= value - 1) {
        setMinusDisabled(true)
      }
    } else if (value) {
      setValue(value - 1)
      value && props.setValue(value - 1)
    }
  }

  const handleInputAmount = (value) => {
    if (props.disabled === true) {
      return
    }
    // remove any chars except numbers
    let validValue = value.replace(/[^0-9]/g, '')
    validValue = parseInt(validValue, 10)
    if (props.minValue !== undefined && props.minValue === validValue) {
      setMinusDisabled(true)
    }
    if (props.maxValue && props.maxValue === validValue) {
      setPlusDisabled(true)
    }
    setValue(validValue)
  }
  React.useEffect(() => {
    if (props.value && props.value !== value) setValue(props.value)
  }, [props.value])

  React.useEffect(() => {
    if (props.maxValue && value && props.maxValue > value) {
      setPlusDisabled(false)
    }

    if (
      (props.minValue !== undefined && value && props.minValue < value) ||
      (props.minValue !== undefined && value === null)
    )
      setMinusDisabled(false)
  }, [value])

  React.useEffect(() => {
    if (value === null || value === props.minValue) {
      setMinusDisabled(true)
    }
  }, [])

  const checkAmount = (value) => {
    // convert string to number
    const validValue = parseInt(value, 10)
    if (value === '' || !value) {
      setValue(props.minValue ? props.minValue : 0)
      props.setValue(props.minValue ? props.minValue : 0)
      props.minValue && setMinusDisabled(true)
      return
    }
    if (props.minValue !== undefined && validValue < props.minValue) {
      setValue(props.minValue)
      props.setValue(props.minValue)
      setMinusDisabled(true)
      return
    }
    if (props.maxValue && value > props.maxValue) {
      setValue(props.maxValue)
      props.setValue(props.maxValue)
      setPlusDisabled(true)
      return
    } else {
      setValue(validValue)
      props.setValue(validValue)
    }
  }

  return (
    <Wrapper className="Amount">
      <AmountButton
        ref={$amountButtonDecrease}
        $disabled={minusDisabled || props.disabled === true}
        className="minus"
        title={props.decrease_button_a11y_title}
        onClick={() => decrement()}
        data-cy-handle={props['data-cy-handle-decrease']}
      >
        <MinusIcon />
      </AmountButton>
      <input
        ref={$input}
        data-cy-handle={props['data-cy-handle-amount']}
        type="number"
        title={props.amount_input_a11y_title}
        onClick={(e) => (e.target as HTMLInputElement).select()}
        onChange={(e) => handleInputAmount(e.target.value)}
        onBlur={(e) => checkAmount(e.target.value)}
        value={value || ''}
        maxLength={4}
      />

      <AmountButton
        ref={$amountButtonIncrease}
        $disabled={plusDisabled || props.disabled === true}
        className="plus"
        title={props.increase_button_a11y_title}
        onClick={() => increment()}
        data-cy-handle={props['data-cy-handle-increase']}
      >
        <PlusIcon />
      </AmountButton>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  gap: ${theme.spacing('xxs')};
  padding: 0;
  margin: 0;
  background: ${theme.colors.white};

  > input {
    ${theme._ty([16, 0, 24], theme.font, '400')}
    border: 1px solid ${theme.colors.b0};
    ${theme.rounding('m')};
    width: 3.25rem;
    text-align: center;
    height: 2.625rem;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type='number'] {
    -moz-appearance: textfield;
  }
`
const AmountButton = styled.button<{ $disabled: boolean }>`
  width: 2.625rem;
  height: 2.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${theme.colors.shade.b3};
  ${theme.rounding('m')}

  > svg > g > g {
    stroke: ${theme.colors.b0};
  }
  ${(props) =>
    props.$disabled &&
    css`
      cursor: default;
      > svg > g > g {
        stroke: ${theme.colors.shade.b3};
      }
    `}
`
